import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useAuth } from "shared/lib/useAuth"
import { useEvent } from "shared/lib/useEvent"
import { ApiResponse } from "shared/types/ApiResponse"

import { TeamMember } from "entities/team-members/types/TeamMember"
import { useCreateProjectTeam } from "features/project-team/api/useCreateProjectTeam"
import { useProjectTeamData } from "features/project-team/model/useProjectTeamData"
import { useTeamMemberFormHandlers } from "features/team-member-management/model/useTeamMemberFormHandlers"
import { CreateTeamMemberFormData } from "features/team-member-management/types/CreateTeamMemberFormData"

import { CreateProjectTeamMembersOverlayModel } from "../CreateProjectTeamMembersOverlay"
import { CreateProjectTeamMembersOverlayConnectorProps } from "../CreateProjectTeamMembersOverlayConnector"

export function useCreateProjectTeamMembersOverlayModel({
  payload: { projectID, defaultValue }
}: CreateProjectTeamMembersOverlayConnectorProps): CreateProjectTeamMembersOverlayModel {
  const { closeOverlay } = useCurrentOverlay()
  const { userInfo } = useAuth()
  const { team } = useProjectTeamData({
    projectID
  })
  const { createProjectTeam } = useCreateProjectTeam()
  const { handleCreateMultipleTeamMembers } = useTeamMemberFormHandlers()

  const handleSubmit = useEvent(
    async (
      formData: CreateTeamMemberFormData
    ): Promise<ApiResponse<TeamMember[]>> => {
      if (!team?.id) {
        const response = await createProjectTeam({
          projectID,
          teamMembers: formData.teamMembers
        })
        return {
          ...response,
          data: response.meta?.memberships ?? null
        }
      }

      return handleCreateMultipleTeamMembers({
        formData,
        teamID: team?.id as string
      })
    }
  )

  // Extract current user email and existing team member emails for validation
  const currentUserEmail = userInfo?.email
  const existingTeamMemberEmails = team?.memberships
    ?.map(member => member.user?.email)
    .filter(Boolean) as string[]

  return {
    defaultValue,
    currentUserEmail,
    existingTeamMemberEmails,
    onSubmit: handleSubmit,
    onCancel: closeOverlay
  }
}
